/* ============================================================
   DW Arcade — shared game client (2D fallback cabinets + 3D scene).
   Talks to the Worker via App Proxy (/apps/arcade/*). Exposes
   window.DwArcade for the 3D layer and emits dwarcade:open/close.

   DEV MODE (section "force-unlock" on -> data-dev="true"): uses the
   debug token count and MOCKS plays locally, so the whole flow works
   on previews/localhost where the App Proxy can't reach. Turn the
   section toggle off for real play. None of this runs in production.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-arcade]');
  if (!root) return;

  var dev = root.getAttribute('data-dev') === 'true';

  var balanceEl = document.querySelector('[data-arcade-balance]');
  var signinLink = document.querySelector('.dw-arcade__credits-signin');
  var isLoggedIn = dev || !!balanceEl;
  var loginUrl = signinLink ? signinLink.getAttribute('href') : '/account/login';

  var overlay = document.querySelector('[data-arcade-overlay]');
  if (!overlay) return;
  var screenEl = overlay.querySelector('.dw-arcade__cab-screen');
  var titleEl = overlay.querySelector('[data-arcade-title]');
  var stageEl = overlay.querySelector('[data-arcade-stage]');
  var msgEl = overlay.querySelector('[data-arcade-msg]');
  var resultEl = overlay.querySelector('[data-arcade-result]');
  var prizeEl = overlay.querySelector('[data-arcade-prize]');
  var codeWrap = overlay.querySelector('[data-arcade-code-wrap]');
  var codeEl = overlay.querySelector('[data-arcade-code]');
  var copyBtn = overlay.querySelector('[data-arcade-copy]');
  var noteEl = overlay.querySelector('[data-arcade-note]');
  var leverBtn = overlay.querySelector('[data-arcade-play]');
  var costEl = overlay.querySelector('[data-arcade-cost]');

  var ACCENT = { wheel: 'var(--green-500)', racing: 'var(--red-500)', claw: 'var(--gold-500)' };
  var balance = 0;     // tokens — earned by spending ($25 = 1), spent on the skill games + vending machine
  var current = null;
  var busy = false;
  var currentPlayId = null; // stashed by startGame, sent back to /claim

  function emit(name) { document.dispatchEvent(new CustomEvent(name, { detail: current })); }
  function setBalance(n) { balance = n; if (balanceEl) balanceEl.textContent = n; if (devInput) devInput.value = n; }
  // Idempotency key for /start + /redeem so a retried request never double-charges or double-issues.
  function uuid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'r-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  // ---- DEV: debug token controls ----
  var devInput = null;
  if (dev) {
    var debug = document.querySelector('[data-arcade-debug]');
    if (debug) {
      devInput = debug.querySelector('[data-dev-tokens]');
      var apply = function (n) { setBalance(Math.max(0, parseInt(n, 10) || 0)); };
      apply(devInput.value || 10);
      devInput.addEventListener('input', function () { apply(devInput.value); });
      var inc = debug.querySelector('[data-dev-inc]'); if (inc) inc.addEventListener('click', function () { apply(balance + 1); });
      var dec = debug.querySelector('[data-dev-dec]'); if (dec) dec.addEventListener('click', function () { apply(balance - 1); });
    }
  }

  function loadBalance() {
    if (dev) return; // dev uses the debug counters
    if (!isLoggedIn) return;
    fetch('/apps/arcade/summary', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (typeof d.balance === 'number') setBalance(d.balance);
      })
      .catch(function () { setBalance(0); });
  }

  function requestPlay(game, cost, name) {
    if (!isLoggedIn) return 'login';
    if (balance < cost) return 'broke';
    openOverlay(game, cost, name);
    return 'ok';
  }

  function openOverlay(game, cost, name) {
    current = { game: game, cost: cost, name: name };
    if (screenEl) screenEl.style.setProperty('--cab-accent', ACCENT[game] || 'var(--green-500)');
    titleEl.textContent = name;
    costEl.textContent = cost + (cost === 1 ? ' token' : ' tokens');
    resetStage();
    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    leverBtn.focus();
    emit('dwarcade:open');
  }

  function resetStage() {
    stageEl.classList.remove('is-playing');
    resultEl.hidden = true;
    codeWrap.hidden = true;
    msgEl.hidden = false;
    msgEl.textContent = 'Ready when you are.';
    leverBtn.disabled = false;
  }

  function closeOverlay() {
    overlay.hidden = true;
    document.documentElement.style.overflow = '';
    emit('dwarcade:close');
    current = null;
  }


  // ---- Skill games: spend a token to START, win a DISCOUNT CODE by SCORE at END ----
  // The player's final SCORE sets the discount tier (server-owned ladder, floored at
  // 5% and capped at 15%). The browser never decides the prize.

  // Spend a token to begin a game. Stashes the server-issued play_id for /claim.
  // Resolves { ok, balance, error }.
  function startGame(game, cost) {
    cost = cost || 1;
    currentPlayId = null;
    if (!isLoggedIn) return Promise.resolve({ ok: false, error: 'not_logged_in' });
    if (balance < cost) return Promise.resolve({ ok: false, error: 'insufficient_tokens', balance: balance });
    if (dev) { setBalance(balance - cost); currentPlayId = 'dev-' + Date.now(); return Promise.resolve({ ok: true, balance: balance }); }
    var rid = uuid();
    return fetch('/apps/arcade/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ game: game, rid: rid }) })
      .then(function (r) { return r.json().then(function (d) {
        if (typeof d.balance === 'number') setBalance(d.balance);
        if (d && d.ok && d.play_id) currentPlayId = d.play_id;
        return d;
      }); })
      .catch(function () { return { ok: false, error: 'network' }; });
  }

  // Claim the discount earned for a final score. Sends the stashed play_id.
  // Resolves { ok, prize:{kind:'discount_pct',value,label}, code, balance, error }.
  function claimScore(game, score) {
    if (dev) {
      var pick = [5, 10, 12, 15][Math.floor(Math.random() * 4)];
      var prize = { kind: 'discount_pct', value: pick, label: pick + '% off' };
      return Promise.resolve({ ok: true, prize: prize, code: 'DW-AR-TEST-' + Math.random().toString(36).slice(2, 6).toUpperCase(), balance: balance });
    }
    return fetch('/apps/arcade/claim', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ play_id: currentPlayId, score: score }) })
      .then(function (r) { return r.json().then(function (d) {
        if (typeof d.balance === 'number') setBalance(d.balance);
        return d;
      }); })
      .catch(function () { return { ok: false, error: 'network' }; });
  }

  // Legacy one-shot overlay play (doPlay/play/finish) retired: the worker's /proxy/play
  // endpoint no longer exists. The live arcade uses startGame() + claimScore() instead.

  function copyCode() {
    var code = codeEl.textContent;
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(function () {
      copyBtn.textContent = 'Copied';
      setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1500);
    });
  }

  // ---- 2D fallback cabinets ----
  root.querySelectorAll('.dwc').forEach(function (cab) {
    function go() {
      if (cab.getAttribute('data-playable') !== 'true') return;
      var res = requestPlay(cab.getAttribute('data-cabinet'), parseInt(cab.getAttribute('data-cost'), 10) || 0, cab.getAttribute('data-name') || 'Play');
      if (res === 'login') { window.location.href = loginUrl; }
      else if (res === 'broke') { cab.classList.remove('is-broke'); void cab.offsetWidth; cab.classList.add('is-broke'); }
    }
    cab.addEventListener('click', go);
    cab.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
  });

  copyBtn.addEventListener('click', copyCode);
  overlay.querySelectorAll('[data-arcade-close]').forEach(function (el) { el.addEventListener('click', closeOverlay); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !overlay.hidden) closeOverlay(); });

  // ---- Rewards machine: spend tokens DIRECTLY for a reward (no game). Catalog is
  // dev-mocked now; the live backend will own pricing + issuance (/apps/arcade/redeem). ----
  var REWARDS = [
    // chip = big value badge · cond = condition line · tier = chip colour group.
    { id: 'p5',    cost: 1, kind: 'discount_pct',    value: 5,         chip: '5%',   tier: 'pct',  label: '5% off your order',   cond: 'Any order' },
    { id: 'p10',   cost: 2, kind: 'discount_pct',    value: 10,        chip: '10%',  tier: 'pct',  label: '10% off your order',  cond: 'Any order' },
    { id: 'a10',   cost: 3, kind: 'discount_amount', value: 10, min: 50, chip: '$10',  tier: 'amt',  label: '$10 off your order',  cond: 'Orders over $50' },
    { id: 'a15',   cost: 4, kind: 'discount_amount', value: 15, min: 75, chip: '$15',  tier: 'amt',  label: '$15 off your order',  cond: 'Orders over $75' },
    { id: 'p15',   cost: 5, kind: 'discount_pct',    value: 15,        chip: '15%',  tier: 'pct',  label: '15% off coupon',      cond: 'Any order' },
    { id: 'p20',   cost: 6, kind: 'discount_pct',    value: 20,        chip: '20%',  tier: 'pct',  label: '20% off coupon',      cond: 'Any order' }
  ];
  // Redeem a catalog item. Resolves { ok, prize, code, balance, error }.
  var redeeming = false;
  function redeem(item) {
    if (!isLoggedIn) return Promise.resolve({ ok: false, error: 'not_logged_in' });
    if (balance < item.cost) return Promise.resolve({ ok: false, error: 'insufficient_tokens', balance: balance });
    if (dev) {
      setBalance(balance - item.cost);
      return Promise.resolve({ ok: true, prize: { kind: item.kind, value: item.value, min: item.min || 0, label: item.label }, code: 'DW-RW-TEST-' + Math.random().toString(36).slice(2, 6).toUpperCase(), balance: balance });
    }
    // In-flight guard: block a second submit until this redemption resolves (pairs with the
    // worker's rid idempotency to make double-charge / double-issue impossible).
    if (redeeming) return Promise.resolve({ ok: false, error: 'busy' });
    redeeming = true;
    var rid = uuid();
    return fetch('/apps/arcade/redeem', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item: item.id, rid: rid }) })
      .then(function (r) { return r.json().then(function (d) { if (typeof d.balance === 'number') setBalance(d.balance); return d; }); })
      .catch(function () { return { ok: false, error: 'network' }; })
      .then(function (d) { redeeming = false; return d; });
  }

  // ---- API for the 2D/3D arcade layer ----
  window.DwArcade = {
    isLoggedIn: isLoggedIn,
    loginUrl: loginUrl,
    dev: dev,
    getBalance: function () { return balance; },
    canPlay: function (cost) { return isLoggedIn && balance >= cost; },
    startGame: startGame,  // spend a token to begin a skill game (stashes play_id)
    claimScore: claimScore,// claim a DISCOUNT CODE for a final score
    rewards: REWARDS,      // vending-machine catalog (tokens → discount)
    redeem: redeem,        // redeem tokens directly for a discount (no game)
    refresh: loadBalance,
    request: requestPlay,  // opens the 2D overlay (Classic view)
    close: closeOverlay
  };

  // ============================================================
  //  The arcade is flat 2D. We still probe the GPU so the one lightweight 3D
  //  piece (the darts board) can dial its quality down on weak/integrated GPUs.
  // ============================================================
  function webglOK() {
    try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); }
    catch (e) { return false; }
  }
  function isWeakGPU() {
    try {
      var c = document.createElement('canvas');
      var gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (!gl) return true;
      var dbg = gl.getExtension('WEBGL_debug_renderer_info');
      var r = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '') : '';
      var lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
      if (!r) return false;
      if (/(Arc\(TM\)|Iris Xe Max)/i.test(r)) return false;
      return /(Intel|HD Graphics|UHD|Iris|Microsoft Basic Render|SwiftShader|llvmpipe|Software)/i.test(r);
    } catch (e) { return false; }
  }
  window.DwArcade.mode = '2d';
  window.DwArcade.hasWebGL = webglOK();
  window.DwArcade.weakGPU = !window.DwArcade.hasWebGL || isWeakGPU();

  loadBalance();
})();
